import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ChefHat, Sparkles, ArrowRight, Upload, Brain, TrendingUp,
  ShieldCheck, Zap, Target, Landmark, FileText, BarChart3,
  Check, AlertTriangle, Utensils, Bike, ShoppingBag,
  Calculator, Cake, Calendar, PackageX, Bell,
} from "lucide-react";

// Helpers for the mock card designs (pure visual, not live data)
function Badge({ children, tone = "gray" }) {
  const tones = {
    gray: "bg-gray-100 text-gray-700 border-gray-200",
    amber: "bg-amber-100 text-amber-800 border-amber-200",
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
    violet: "bg-violet-100 text-violet-800 border-violet-200",
    sky: "bg-sky-100 text-sky-800 border-sky-200",
    red: "bg-red-100 text-red-800 border-red-200",
  };
  return <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${tones[tone]}`}>{children}</span>;
}

export default function FinanceLandingPage() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-gray-200 bg-white/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center">
              <ChefHat className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-semibold text-lg">MenuGenius</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/"><Button variant="ghost" size="sm">Volver</Button></Link>
            <Link to="/register"><Button size="sm" data-testid="finance-landing-signup">Prueba gratis 14 días <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-50 via-white to-indigo-50" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 lg:pt-32 lg:pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-violet-100 text-violet-800 border border-violet-200 mb-5">
              <Sparkles className="h-3.5 w-3.5" /> Módulo Contabilidad · IA sectorial
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              El único SaaS contable que te dice <span className="bg-gradient-to-br from-violet-600 to-indigo-600 bg-clip-text text-transparent">por qué</span> estás por encima o por debajo de la media de tu sector.
            </h1>
            <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-xl">
              Sube una factura o un extracto bancario. Dejar de perder horas cuadrando gastos. Descubre cada mes si tu food cost, margen y gasto operativo están en línea con restaurantes como el tuyo.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register">
                <Button size="lg" className="text-base" data-testid="finance-landing-cta-hero">
                  Prueba gratis 14 días <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </Link>
              <a href="#como-funciona">
                <Button size="lg" variant="outline" className="text-base">Ver cómo funciona</Button>
              </a>
            </div>
            <div className="mt-6 flex items-center gap-6 text-sm text-gray-500">
              <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-500" />Sin tarjeta</span>
              <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-500" />Setup en 5 min</span>
              <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-500" />Cancela cuando quieras</span>
            </div>
          </div>

          {/* Mock insights card (visual only) */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-violet-200/50 to-indigo-200/30 rounded-3xl blur-2xl" aria-hidden />
            <Card className="relative border-violet-200 bg-gradient-to-br from-violet-50/60 to-indigo-50/60 shadow-xl">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-violet-600" />
                  <h3 className="font-semibold text-sm">Insights del asesor IA</h3>
                  <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-white/60 border border-violet-200 text-violet-700">
                    actualizado hace 2h
                  </span>
                </div>
                <div className="grid md:grid-cols-2 gap-2.5">
                  <MockInsight
                    Icon={AlertTriangle}
                    tone="amber"
                    title="Food cost 9 pts sobre el sector"
                    body="Tu 38.5% vs 29.1% de la mediana de trattorias similares. Revisa compras de Makro."
                  />
                  <MockInsight
                    Icon={TrendingUp}
                    tone="emerald"
                    title="Ventas +84% este mes"
                    body="Pasaste de 5.114 € a 9.395 € con 166 tickets. Buen impulso comercial."
                  />
                  <MockInsight
                    Icon={Target}
                    tone="sky"
                    title="Brecha con sector: 71% por debajo"
                    body="Tus 9.395 € vs 32.730 € de media sectorial. Oportunidad de crecimiento."
                  />
                  <MockInsight
                    Icon={Utensils}
                    tone="violet"
                    title="100% de ventas en sala"
                    body="Considera activar delivery o takeaway para diversificar."
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Diferencial */}
      <section id="como-funciona" className="py-20 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center mb-14">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold">Lo que ningún software contable hace</h2>
            <p className="mt-4 text-gray-600 text-lg">
              Cualquiera puede mostrarte un P&L. Nosotros te decimos si está bien o mal comparado con tus pares.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <FeatureCard
              Icon={Brain}
              title="Comparativa anónima con tu sector"
              body="Tus KPIs se comparan mensualmente con restaurantes del mismo tipo y tamaño. Claude Sonnet 4.5 genera insights concretos citando tus cifras reales vs la mediana sectorial. Datos 100% anonimizados."
              highlight
            />
            <FeatureCard
              Icon={Upload}
              title="Digitalización de facturas con IA"
              body="Fotografía, PDF o arrastra. VirusTotal escanea, Gemini 2.5 Flash extrae proveedor, fecha, importe, IVA y categoría. Tú solo confirmas. Aprende tus proveedores en 2 facturas."
            />
            <FeatureCard
              Icon={Landmark}
              title="Reconciliación bancaria universal"
              body="Sube cualquier CSV de tu banco (Santander, BBVA, CaixaBank, cualquiera). La IA detecta columnas, empareja con tus gastos y propone coincidencias. Reglas aprendidas que se comparten entre tus locales."
            />
          </div>
        </div>
      </section>

      {/* Proceso paso a paso */}
      <section className="py-20 bg-gray-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold">De una factura a un P&L vivo en 30 segundos</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { n: 1, Icon: Upload,     title: "Subes una factura",  body: "Arrastra o haz foto. VirusTotal la escanea antes de procesarla." },
              { n: 2, Icon: Sparkles,   title: "La IA extrae todo",  body: "Gemini lee proveedor, fecha, importe, IVA y sugiere categoría." },
              { n: 3, Icon: Check,      title: "Confirmas con 1 clic", body: "O ajustas si hace falta. El sistema aprende para la próxima." },
              { n: 4, Icon: BarChart3,  title: "Tu P&L se actualiza", body: "Ingresos, food cost, margen y comparativa sectorial al instante." },
            ].map(s => (
              <div key={s.n} className="relative">
                <div className="h-full bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-md transition">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-bold text-violet-600">PASO {s.n}</span>
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>
                  <s.Icon className="h-8 w-8 text-violet-600 mb-3" />
                  <h3 className="font-semibold mb-1">{s.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold">Todo lo que necesitas para no volver a cuadrar cuentas</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <CapCard Icon={FileText}     title="Buzón de facturas"           body="Bandeja con estado 'Por revisar / Procesados'. Preview de la imagen y campos editables lado a lado." />
            <CapCard Icon={BarChart3}    title="P&L con tendencia 12 meses" body="KPIs clickables, comparativa mes a mes con deltas, ingresos por canal (sala/delivery/takeaway)." />
            <CapCard Icon={Brain}        title="Reglas aprendidas"           body="Si marcas 2 veces una transacción como 'Endesa → utilities', la siguiente se clasifica sola." />
            <CapCard Icon={ShieldCheck}  title="Sin integraciones bancarias" body="Tu banco no aparece. Solo subes el CSV cuando tú quieras. Tus datos viven solo en tu cuenta." />
            <CapCard Icon={Zap}          title="Reglas cross-restaurant"     body="Tienes varios locales? Las reglas que aprendes en uno se aplican en todos con un toggle." />
            <CapCard Icon={Bell}         title="Avisos configurables"        body="Email + campanita in-app. 5 canales de alertas, cada uno activable independientemente por restaurante." />
          </div>
        </div>
      </section>

      {/* Visual: Channel breakdown + trend mock */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-white border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold">Tu rentabilidad real, sin esperar al gestor</h2>
            <p className="mt-5 text-gray-600 text-lg leading-relaxed">
              Cada venta, cada ticket, cada gasto se integran al instante en tu P&L. Ves cómo evoluciona el margen mes a mes, qué canal crece más y dónde se te va el dinero.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm">
              {[
                "Desglose de ingresos por canal con porcentajes visuales",
                "Tendencia de 12 meses (ingresos + beneficio neto)",
                "4 KPIs clickables que navegan al detalle",
                "Delta vs. mes anterior con semántica invertida en costes",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />{t}</li>
              ))}
            </ul>
          </div>

          {/* Channel breakdown mock */}
          <Card className="border-gray-200 shadow-xl">
            <CardContent className="p-5 space-y-4">
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Utensils className="h-4 w-4 text-gray-700" />
                  <span className="text-sm font-semibold">Ingresos por canal</span>
                </div>
                <div className="flex h-2 w-full rounded-full overflow-hidden bg-gray-100">
                  <div className="bg-emerald-500" style={{ width: "62%" }} />
                  <div className="bg-sky-500"     style={{ width: "24%" }} />
                  <div className="bg-amber-500"   style={{ width: "14%" }} />
                </div>
                <div className="mt-3 space-y-1.5 text-sm">
                  <MiniRow Icon={Utensils}    label="Sala"     pct="62%" eur="5.824 €" tone="emerald" />
                  <MiniRow Icon={Bike}        label="Delivery" pct="24%" eur="2.255 €" tone="sky" />
                  <MiniRow Icon={ShoppingBag} label="Recoger"  pct="14%" eur="1.315 €" tone="amber" />
                </div>
              </div>
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <BarChart3 className="h-4 w-4 text-gray-700" />
                  <span className="text-sm font-semibold">Tendencia 12 meses</span>
                </div>
                <svg viewBox="0 0 420 140" className="w-full h-28">
                  {[2100,2300,1900,2700,3200,2900,3400,4000,4200,5100,9400,9700].map((v, i) => {
                    const max = 9700, H = 140, P = 18, W = 420, n = 12;
                    const bw = (W - 2 * P) / n;
                    const y = H - P - (v / max) * (H - 2 * P);
                    return <rect key={i} x={P + bw * i + 2} y={y} width={bw - 4} height={H - P - y} rx="2" className="fill-emerald-300" />;
                  })}
                  <path
                    d="M 33.5,105 L 63.5,102 L 93.5,110 L 123.5,95 L 153.5,88 L 183.5,92 L 213.5,82 L 243.5,70 L 273.5,65 L 303.5,45 L 333.5,20 L 363.5,18"
                    stroke="#4f46e5" fill="none" strokeWidth="1.5"
                  />
                </svg>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Notifs preview grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold">Las alertas que importan, cuando importan</h2>
            <p className="mt-4 text-gray-600 text-lg max-w-2xl mx-auto">
              5 canales separados, activables por restaurante. Email o campanita in-app. Tú decides.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ChannelCard Icon={Calculator}   tone="indigo"  title="Resumen semanal P&L" body="Domingos 18:00. KPIs clave + alertas del asesor IA." />
            <ChannelCard Icon={AlertTriangle} tone="amber"  title="Mermas graves"         body="Al instante cuando se detecta pérdida oculta >15%." />
            <ChannelCard Icon={Calendar}     tone="red"     title="Facturas por vencer"   body="Cada mañana 09:00 si hay pagos en los próximos 2 días." />
            <ChannelCard Icon={Cake}         tone="pink"    title="Cumpleaños VIP"         body="Nombres y contactos de clientes que cumplen hoy." />
            <ChannelCard Icon={Brain}        tone="violet"  title="Comparativas sectoriales" body="Contribución anónima al benchmark colectivo." />
            <ChannelCard Icon={PackageX}     tone="sky"     title="Stock bajo mínimos"     body="Productos que necesitan reposición urgente." />
          </div>
        </div>
      </section>

      {/* Trust / privacy */}
      <section className="py-16 bg-gray-50 border-y border-gray-100">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <div className="shrink-0 h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-emerald-700" />
            </div>
            <div>
              <h3 className="font-heading text-xl font-semibold mb-2">Privacidad y seguridad, por diseño</h3>
              <p className="text-gray-700 leading-relaxed">
                El benchmarking sectorial funciona con datos <strong>completamente anonimizados</strong>: solo compartimos tipo de cocina y bucket de tamaño, nunca el nombre, dirección o clientes de tu restaurante. Requerimos un mínimo de 5 restaurantes por bucket antes de mostrar estadísticas para prevenir la identificación. Cada factura pasa por VirusTotal antes de tocar nuestros sistemas. Cumplimos con RGPD y puedes exportar o borrar tus datos cuando quieras.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight">Deja de cuadrar cuentas a ciegas</h2>
          <p className="mt-5 text-lg text-gray-600">
            Únete al único SaaS contable que te compara con tu sector. 14 días gratis, sin tarjeta.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/register">
              <Button size="lg" className="text-base" data-testid="finance-landing-cta-footer">
                Empezar ahora <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline" className="text-base">Ver plataforma completa</Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-8 border-t border-gray-100 text-center text-sm text-gray-500">
        © {new Date().getFullYear()} MenuGenius · <Link to="/" className="underline hover:text-gray-700">Volver al inicio</Link>
      </footer>
    </div>
  );
}

// ─── Small subcomponents ──────────────────────────────────────────────

function MockInsight({ Icon, tone, title, body }) {
  const tones = {
    amber:   { card: "border-amber-200 bg-amber-50/70",   iconBg: "bg-amber-100 text-amber-800",   title: "text-amber-900" },
    emerald: { card: "border-emerald-200 bg-emerald-50/70", iconBg: "bg-emerald-100 text-emerald-700", title: "text-emerald-900" },
    sky:     { card: "border-sky-200 bg-sky-50/70",       iconBg: "bg-sky-100 text-sky-700",       title: "text-sky-900" },
    violet:  { card: "border-violet-200 bg-violet-50/70", iconBg: "bg-violet-100 text-violet-700", title: "text-violet-900" },
  }[tone] || {};
  return (
    <div className={`rounded-xl border ${tones.card} p-3 flex gap-2.5`}>
      <div className={`shrink-0 h-8 w-8 rounded-lg flex items-center justify-center ${tones.iconBg}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold ${tones.title}`}>{title}</div>
        <div className="text-xs text-gray-700 mt-0.5 leading-snug">{body}</div>
      </div>
    </div>
  );
}

function FeatureCard({ Icon, title, body, highlight }) {
  return (
    <div className={`rounded-2xl p-6 transition hover:shadow-lg ${highlight ? "border-2 border-violet-300 bg-gradient-to-br from-violet-50 to-indigo-50" : "border border-gray-200 bg-white"}`}>
      <div className={`inline-flex h-12 w-12 rounded-xl items-center justify-center mb-4 ${highlight ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-700"}`}>
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="font-heading text-xl font-semibold mb-2">{title}</h3>
      <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
    </div>
  );
}

function CapCard({ Icon, title, body }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 hover:border-violet-200 hover:shadow-sm transition">
      <Icon className="h-5 w-5 text-violet-600 mb-3" />
      <h3 className="font-semibold mb-1 text-gray-900">{title}</h3>
      <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
    </div>
  );
}

function ChannelCard({ Icon, tone, title, body }) {
  const tones = {
    indigo: "text-indigo-600 bg-indigo-50 border-indigo-200",
    amber:  "text-amber-700 bg-amber-50 border-amber-200",
    red:    "text-red-700 bg-red-50 border-red-200",
    pink:   "text-pink-600 bg-pink-50 border-pink-200",
    violet: "text-violet-600 bg-violet-50 border-violet-200",
    sky:    "text-sky-600 bg-sky-50 border-sky-200",
  };
  return (
    <div className={`rounded-xl border p-5 ${tones[tone] || "text-gray-700 bg-gray-50 border-gray-200"}`}>
      <Icon className="h-6 w-6 mb-3" />
      <h3 className="font-semibold mb-1">{title}</h3>
      <p className="text-sm opacity-80 leading-relaxed">{body}</p>
    </div>
  );
}

function MiniRow({ Icon, label, pct, eur, tone }) {
  const tones = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    sky:     "text-sky-700 bg-sky-50 border-sky-200",
    amber:   "text-amber-700 bg-amber-50 border-amber-200",
  };
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border ${tones[tone]}`}>
        <Icon className="h-3 w-3" /> {label}
      </span>
      <div className="flex-1 text-xs text-gray-500">{pct}</div>
      <div className="font-medium">{eur}</div>
    </div>
  );
}
